import uuid from "react-native-uuid";
import { convertToRaw, convertFromRaw } from "draft-js";

import { stateFromHTML } from "./stateFromHTML";
import {
  DOCUMENT_EXTRACTION,
  DRAFT_ENTITY,
  FONT_WEIGHT_OPTIONS_MAP,
  FONT_FAMILY_OPTIONS_MAP,
  HEADER_FONT_SIZES,
  FONT_FAMILY_WEIGHT_MAPPING
} from "../../constants/document";
import { INLINE_ELEMENTS } from "../../constants/stateFromElement";

import { rgb2hex } from "../colorUtils";
import {
  parseFontSize,
  parseFontWeight,
  parseFontStyle,
} from "./cssParserUtils";

const handleGoogleDocsBoldCase = newDiv => {
  // In google docs everything is wrapped in b so we remove it before processing
  if (
    newDiv.firstElementChild &&
    newDiv.firstElementChild.tagName === "B" &&
    newDiv.firstElementChild.style &&
    newDiv.firstElementChild.style.fontWeight === "normal"
  ) {
    // modern browsers
    return newDiv.firstElementChild.innerHTML;
  } else if (
    newDiv.firstChild &&
    newDiv.firstChild.tagName === "B" &&
    newDiv.firstChild.style &&
    newDiv.firstChild.style.fontWeight === "normal"
  ) {
    //older IE
    return newDiv.firstChild.innerHTML;
  } else if (
    newDiv.children &&
    newDiv.children[0] &&
    newDiv.children[0].tagName === "B" &&
    newDiv.children[0].style &&
    newDiv.children[0].style.fontWeight === "normal"
  ) {
    //new IE
    return newDiv.children[0].innerHTML;
  }
  return newDiv.innerHTML;
};

// removes nested p tags from list
const sanitizeList = listDiv => {
  if (listDiv.hasChildNodes()) {
    let ulChildren = listDiv.childNodes;
    for (let j = 0; j < ulChildren.length; j++) {
      if (ulChildren[j].tagName === "LI" && ulChildren[j].hasChildNodes()) {
        let children = ulChildren[j].childNodes;
        for (let i = 0; i < children.length; i++) {
          if (typeof window !== "undefined" && children[i].tagName === "P" || children[i].tagName === "BR") {
            var d = document.createElement("span");
            d.innerHTML = children[i].innerHTML;
            children[i].parentNode.replaceChild(d, children[i]);
          } else if (
            children[i].tagName === "OL" ||
            children[i].tagName === "UL"
          ) {
            children[i].innerHTML = sanitizeList(children[i]);
          }
        }
      } else if (
        ulChildren[j].tagName === "OL" ||
        ulChildren[j].tagName === "UL"
      ) {
        ulChildren[j].innerHTML = sanitizeList(ulChildren[j]);
      }
    }
  }
  return listDiv.innerHTML;
};

const handleListWithPTag = newDiv => {
  if (newDiv.hasChildNodes()) {
    let children = newDiv.childNodes;
    for (let i = 0; i < children.length; i++) {
      //for every element
      if (children[i].tagName === "OL" || children[i].tagName === "UL") {
        children[i].innerHTML = sanitizeList(children[i]);
      }
    }
  }
  return newDiv.innerHTML;
};

const handleSpecialHTMLCases = docHTML => {
  if (typeof window === "undefined"){
    return docHTML
  }
  var newDiv = document.createElement("div");
  newDiv.innerHTML = docHTML;

  // In google docs everything is wrapped in b so we remove it before processing
  newDiv.innerHTML = handleGoogleDocsBoldCase(newDiv);

  // in google docs each li element has p tag which causes split block
  newDiv.innerHTML = handleListWithPTag(newDiv);

  let nodeArray = [...newDiv.childNodes];
  const hasWrapperBlock = nodeArray
    ? nodeArray.every(childNode => {
        return (
          !childNode.tagName ||
          !INLINE_ELEMENTS.hasOwnProperty(childNode.tagName.toLowerCase())
        );
      })
    : false;
  return hasWrapperBlock ? newDiv.innerHTML : `<div>${newDiv.innerHTML}</div>`;
};

const getBlockLevelStylesRanges = (existinStyles, newStyle, blockLength) => {
  if (!existinStyles || existinStyles.length === 0) {
    return [
      {
        offset: 0,
        length: blockLength,
        style: newStyle
      }
    ];
  }

  const inlineStyleArr = [];
  let start = 0;
  let end = blockLength;
  existinStyles.sort((a, b) => {
    if (a.offset === b.offset) {
      return a.style > b.style ? 1 : -1;
    }
    return a.offset > b.offset ? 1 : -1;
  });

  for (let i = 0; i < existinStyles.length; i++) {
    let inlineStyle = existinStyles[i];
    if (start < inlineStyle.offset && inlineStyle.offset > 0) {
      const styleLength = existinStyles[i].offset - 1 - start;
      inlineStyleArr.push({
        offset: start,
        length: styleLength,
        style: newStyle
      });
    }
    start = inlineStyle.offset + inlineStyle.length + 1;
  }

  if (start < blockLength) {
    inlineStyleArr.push({
      offset: start,
      length: blockLength - start,
      style: newStyle
    });
  }

  return inlineStyleArr;
};

const convertedElementStylesToInline = contentState => {
  const raw = convertToRaw(contentState);
  const { blocks: updatedBlocks, entityMap } = raw;
  // remove empty blocks added at the start due to google docs wrapper
  if (updatedBlocks && updatedBlocks.length > 1) {
    while (
      updatedBlocks &&
      updatedBlocks.length > 0 &&
      (!updatedBlocks[0].text || !updatedBlocks[0].text.trim())
    ) {
      updatedBlocks.shift();
    }
  }

  updatedBlocks.forEach(block => {
    if (block.text && block.data && block.data.elementStyle) {
      let styleArr = block.data.elementStyle.split(";");

      if (styleArr && styleArr.length > 0) {
        let inlineStyleArr = [];
        styleArr.forEach(style => {
          //Font Size
          if (style.includes("font-size")) {
            const value =
              style.split(":").length > 1 ? style.split(":")[1] : "";

            const size = parseFontSize(value);
            if (size) {
              if (
                block.inlineStyleRanges &&
                Array.isArray(block.inlineStyleRanges)
              ) {
                const existingStyles = block.inlineStyleRanges.filter(
                  blockStyle =>
                    blockStyle && blockStyle.style.includes("FONTSIZE")
                );
                const adjustedStyles = getBlockLevelStylesRanges(
                  existingStyles,
                  size,
                  block.text.length
                );
                if (adjustedStyles) {
                  inlineStyleArr.push(...adjustedStyles);
                }
              } else {
                inlineStyleArr.push({
                  offset: 0,
                  length: block.text.length,
                  style: size
                });
              }
            }
          }

          //Text Highlight
          if (style.includes("backgroundColor:") || style.includes("backgroundColor :")) {
            const value =
              style.split(":").length > 1 ? style.split(":")[1] : "";
            const key = style.split(":").length > 0 ? style.split(":")[0] : "";
            if (value && key === "backgroundColor" && value !== "inherit") {
              const hexColor = rgb2hex(value);
              if (hexColor) {
                if (
                  block.inlineStyleRanges &&
                  Array.isArray(block.inlineStyleRanges)
                ) {
                  const existingStyles = block.inlineStyleRanges.filter(
                    blockStyle =>
                      blockStyle && blockStyle.style.includes("FONTTEXTHIGHLIGHT")
                  );
                  const adjustedStyles = getBlockLevelStylesRanges(
                    existingStyles,
                    `FONTTEXTHIGHLIGHT_${hexColor.substring(1, 7)}`,
                    block.text.length
                  );
                  if (adjustedStyles) {
                    inlineStyleArr.push(...adjustedStyles);
                  }
                } else {
                  inlineStyleArr.push({
                    offset: 0,
                    length: block.text.length,
                    style: `FONTTEXTHIGHLIGHT_${hexColor.substring(1, 7)}`
                  });
                }
              }
            }
          }

          //Font Color
          if (style.includes("color:") || style.includes("color :")) {
            const value =
              style.split(":").length > 1 ? style.split(":")[1] : "";
            const key = style.split(":").length > 0 ? style.split(":")[0] : "";
            if (value && key === "color" && value !== "inherit") {
              const hexColor = rgb2hex(value);
              if (hexColor) {
                if (
                  block.inlineStyleRanges &&
                  Array.isArray(block.inlineStyleRanges)
                ) {
                  const existingStyles = block.inlineStyleRanges.filter(
                    blockStyle =>
                      blockStyle && blockStyle.style.includes("FONTCOLOR")
                  );
                  const adjustedStyles = getBlockLevelStylesRanges(
                    existingStyles,
                    `FONTCOLOR_${hexColor.substring(1, 7)}`,
                    block.text.length
                  );
                  if (adjustedStyles) {
                    inlineStyleArr.push(...adjustedStyles);
                  }
                } else {
                  inlineStyleArr.push({
                    offset: 0,
                    length: block.text.length,
                    style: `FONTCOLOR_${hexColor.substring(1, 7)}`
                  });
                }
              }
            }
          }

          // Font Family
          let fontFamily;
          if (style.includes("font-family")) {
            let value = style.split(":").length > 1 ? style.split(":")[1] : "";
            value = value.includes(",") ? value.split(",")[0] : value;
            value = value.replace(/"/g, ""); // (ex: ""Courier New"" ==> "Courier New")
            value = value.replace(/'/g, ""); // (ex: "'Courier New'" ==> "Courier New")
            value = value.trim();
            fontFamily = FONT_FAMILY_OPTIONS_MAP[value];
            if (fontFamily) {
              if (
                block.inlineStyleRanges &&
                Array.isArray(block.inlineStyleRanges)
              ) {
                const existingStyles = block.inlineStyleRanges.filter(
                  blockStyle =>
                    blockStyle && blockStyle.style.includes("FONTFAMILY")
                );
                const adjustedStyles = getBlockLevelStylesRanges(
                  existingStyles,
                  fontFamily,
                  block.text.length
                );
                if (adjustedStyles) {
                  inlineStyleArr.push(...adjustedStyles);
                }
              } else {
                inlineStyleArr.push({
                  offset: 0,
                  length: block.text.length,
                  style: fontFamily
                });
              }
            }
          }

          //Font Weight
          if (style.includes("font-weight")) {
            let value = style.split(":").length > 1 ? style.split(":")[1] : "";
            value = value.trim();
            const fontWeight = parseFontWeight(value);
            let isWeightSupported = false;
            const weightOptions = FONT_FAMILY_WEIGHT_MAPPING[fontFamily];
            if (weightOptions) {
              const weightOption = weightOptions.filter(
                style => style && style.style === fontWeight
              );
              isWeightSupported =
                weightOption && weightOption.length > 0 ? true : false;
            }
            if (fontWeight && isWeightSupported) {
              if (
                block.inlineStyleRanges &&
                Array.isArray(block.inlineStyleRanges)
              ) {
                const existingStyles = block.inlineStyleRanges.filter(
                  blockStyle =>
                    blockStyle && blockStyle.style.includes("FONTWEIGHT")
                );
                const adjustedStyles = getBlockLevelStylesRanges(
                  existingStyles,
                  fontWeight,
                  block.text.length
                );
                if (adjustedStyles) {
                  inlineStyleArr.push(...adjustedStyles);
                }
              } else {
                inlineStyleArr.push({
                  offset: 0,
                  length: block.text.length,
                  style: fontWeight
                });
              }
            }
          }

          // Text decoration underline
          if (style.includes("text-decoration")) {
            let value = style.split(":").length > 1 ? style.split(":")[1] : "";
            value = value.trim();

            if (value === "underline") {
              inlineStyleArr.push({
                offset: 0,
                length: block.text.length,
                style: "UNDERLINE"
              });
            }
          }

          // font-style: italic detection
          if (style.includes("font-style")) {
            const parsedStyle = parseFontStyle(style);
            if (parsedStyle) {
              inlineStyleArr.push({
                offset: 0,
                length: block.text.length,
                style: parsedStyle
              });
            }
          }
        });

        //remove processed styles
        styleArr = styleArr.filter(
          style =>
            !style.includes("font-weight") &&
            !style.includes("color") &&
            !style.includes("font-size") &&
            !style.includes("font-family") &&
            !style.includes("background-color")
        );

        //set inline style ranges in block
        if (block.inlineStyleRanges && Array.isArray(block.inlineStyleRanges)) {
          block.inlineStyleRanges.push(...inlineStyleArr);
        } else {
          block.inlineStyleRanges = inlineStyleArr;
        }

        const headerMap = {
          "header-one": "H1",
          "header-two": "H2",
          "header-three": "H3",
          "header-four": "H4"
        };

        // remove empty/null styles
        if (block.inlineStyleRanges && Array.isArray(block.inlineStyleRanges)) {
          block.inlineStyleRanges = block.inlineStyleRanges.filter(
            style => style.style
          );
        }

        block.data.elementStyle = styleArr.join(";");
      }
    }

    // only keep supported & unprocessed styles, remove everything else
    // e.g. things like position: absolute creates issue while rendering
    if (block.data.elementStyle) {
      let styleArr = block.data.elementStyle.split(";");
      if (styleArr && styleArr.length > 0) {
        styleArr = styleArr.filter(
          style =>
            style.includes("text-align") ||
            style.includes("text-indent") ||
            style.includes("margin-left") || style.includes("line-height")
        );
      }
      block.data.elementStyle = styleArr.join(";");
    }
  });

  raw.blocks = updatedBlocks;
  return convertFromRaw(raw);
};

const handleNegativeIndent = elementStyle => {
  //handle text indent
  if (
    elementStyle.includes("text-indent") ||
    elementStyle.includes("margin-left")
  ) {
    let styleArr = elementStyle.split(";");
    if (styleArr && styleArr.length > 0) {
      styleArr = styleArr.filter(style => {
        const value = style.split(":").length > 1 ? style.split(":")[1] : "";
        return !(value && parseInt(value) < 0);
      });
      styleArr = styleArr.filter(style => style);
    }
    elementStyle = styleArr.join(";");
  }
  return elementStyle;
};

// convert short hand font property to multiple properties
const processShortHandProperty = (elementStyle, element) => {
  if (!elementStyle || !element || !element.style) return elementStyle;

  // font size
  if (!elementStyle.includes("font-size") && element.style.fontSize) {
    elementStyle = elementStyle.endsWith(";")
      ? `${elementStyle}font-size:${element.style.fontSize};`
      : `${elementStyle};font-size:${element.style.fontSize};`;
  }

  // font family
  if (!elementStyle.includes("font-family") && element.style.fontFamily) {
    elementStyle = elementStyle.endsWith(";")
      ? `${elementStyle}font-family:${element.style.fontFamily};`
      : `${elementStyle};font-family:${element.style.fontFamily};`;
  }

  // font weight
  if (!elementStyle.includes("font-weight") && element.style.fontWeight) {
    elementStyle = elementStyle.endsWith(";")
      ? `${elementStyle}font-weight:${element.style.fontWeight};`
      : `${elementStyle};font-weight:${element.style.fontWeight};`;
  }

  // font-style is usefull only for detecting italics
  if (
    !elementStyle.includes("font-style") &&
    element.style.fontStyle &&
    element.style.fontStyle === "italic"
  ) {
    elementStyle = elementStyle.endsWith(";")
      ? `${elementStyle}font-style:${element.style.fontStyle};`
      : `${elementStyle};font-style:${element.style.fontStyle};`;
  }

  elementStyle = handleNegativeIndent(elementStyle);

  // remove font shorthand property after processing
  let splitStyles = elementStyle.split(";");
  splitStyles = splitStyles.filter(
    style => !style.includes("font:") && !style.includes("font :")
  );
  return splitStyles.join(";");
};

// apply UL or OL styles to LI if it doenst exists
const applyListParentStylesToChildren = (parentStyles, childStyles) => {
  childStyles = childStyles ? childStyles : "";
  if (parentStyles && parentStyles.split(";").length > 0) {
    const styleArr = parentStyles.split(";");
    styleArr.forEach(style => {
      //font size
      if (style.includes("font-size") && !childStyles.includes("font-size")) {
        childStyles = childStyles.endsWith(";")
          ? `${childStyles}${style};`
          : `${childStyles};${style};`;
      }

      //font color
      if (style.includes("color") && !childStyles.includes("color")) {
        childStyles = childStyles.endsWith(";")
          ? `${childStyles}${style};`
          : `${childStyles};${style};`;
      }

      //text highlight
      if (style.includes("background-color") && !childStyles.includes("background-color")) {
        childStyles = childStyles.endsWith(";")
          ? `${childStyles}${style};`
          : `${childStyles};${style};`;
      }

      //font family
      if (
        style.includes("font-family") &&
        !childStyles.includes("font-family")
      ) {
        childStyles = childStyles.endsWith(";")
          ? `${childStyles}${style};`
          : `${childStyles};${style};`;
      }

      //font weight
      if (
        style.includes("font-weight") &&
        !childStyles.includes("font-weight")
      ) {
        childStyles = childStyles.endsWith(";")
          ? `${childStyles}${style};`
          : `${childStyles};${style};`;
      }
    });
  }
  return childStyles;
};

const customBlockFn = element => {
  if (element.tagName === "LINE_BREAK") {
    return {
      data: {},
      type: "unstyled"
    };
  }

  if (element.tagName === "PLIST") {
    const numbering = element.getAttribute("numbering");
    let elementStyle = element.getAttribute("style");
    elementStyle = processShortHandProperty(elementStyle, element);
    const ordered = element.getAttribute("ordered");
    const type = element.getAttribute("type");

    if (ordered === "False") {
      element.innerHTML = `${element.innerHTML}`;
    } else if (numbering) {
      element.innerHTML = `${element.innerHTML}`;
    }
    const data = {
      numbering,
      level: element.className,
      elementStyle
    };

    if (type) {
      return {
        data,
        type
      };
    }

    const classes = element.getAttribute("class");
    const levelClass = classes
      .split(" ")
      .filter(item => item.includes("list-level-"));
    let level = 0;
    if (levelClass && levelClass.length > 0) {
      const levelParts = levelClass[0].split("-");
      if (levelParts.length > 2) {
        const { 2: second } = levelParts;
        level = second;
      }
    }
    return ordered === "False"
      ? {
          data,
          type: `unordered-list-${level}`
        }
      : {
          data,
          type: `ordered-list-${level}`
        };
  }

  if (element.tagName === "LI") {
    // Getting the class name of list element and checking if it is a revv list class name,
    // if it is there then, return the class name as a list type
    const liClassName = element.getAttribute("classname");
    let type = "";
    if (
      liClassName &&
      (liClassName.includes("unordered-list") ||
        liClassName.includes("ordered-list"))
    ) {
      type = liClassName;
    }
    if (!type) {
      // If there is no class name found in the list element,
      // then getting the parent element type(UL/OL) to find the corresponding list type
      const parentEle = element.parentElement;
      let parentEleType = null;
      if (parentEle) {
        parentEleType = parentEle.nodeName;
        if (parentEle.nodeName === "DIV") {
          parentEleType = parentEle.parentElement.nodeName;
        }
      }
      if (parentEleType === "OL") {
        type = "ordered-list-1";
      } else if (parentEleType === "UL") {
        type = "unordered-list-1";
      }
    }
    /**
     * Since, we are storing the block styles in a data key, so need to remove the
     * following styles from list elements -
     * "display", "list-style" , "list-style-type", etc
     * if don't remove, double styles will be applied for list elements,
     * which creates UI rendering issue
     */
    let getAllEleStyles = element.getAttribute("style");
    getAllEleStyles = processShortHandProperty(getAllEleStyles, element);

    //since UL & OL tags are ignored in draftjs, their style needs to be added to its children
    if (element.parentElement) {
      const parentEle = element.parentElement;
      const parentStyles = parentEle.getAttribute("style");
      if (parentStyles) {
        getAllEleStyles = applyListParentStylesToChildren(
          parentStyles,
          getAllEleStyles
        );
      }
    }

    let elementStyle = getAllEleStyles;
    if (getAllEleStyles) {
      const splitStyles = getAllEleStyles.split(";");
      if (splitStyles && splitStyles.length > 0) {
        const filterStyles = splitStyles.filter(
          style =>
            !(
              style.includes("display") ||
              style.includes("list-item") ||
              style.includes("list-style") ||
              style.includes("list-style-type")
            )
        );
        elementStyle = filterStyles.join(";");
      }
    }
    const data = {
      elementStyle
    };
    return {
      data,
      type
    };
  }

  if (
    element.tagName === "P" ||
    element.tagName === "SPAN" ||
    element.tagName === "DIV" ||
    element.tagName === "BODY"
  ) {
    let elementStyle = element.getAttribute("style");
    elementStyle = processShortHandProperty(elementStyle, element);
    const data = {
      elementStyle
    };
    return {
      data,
      type: "unstyled"
    };
  }

  if (
    element.tagName === "H1" ||
    element.tagName === "H2" ||
    element.tagName === "H3" ||
    element.tagName === "H4"
  ) {
    // remove font-size & font-weight to avoid conflict with heading style
    let elementStyle = element.getAttribute("style");
    elementStyle = processShortHandProperty(elementStyle, element);
    let hasFontSize = false;
    if (elementStyle) {
      let styleArr = elementStyle.split(";");
      styleArr = styleArr.filter(style => !style.includes("font-weight"));
      const fontSizeStyle = styleArr.filter(
        style => style && style.includes("font-size")
      );
      if (fontSizeStyle && fontSizeStyle.length > 0) {
        const value =
          fontSizeStyle[0].split(":").length > 1
            ? fontSizeStyle[0].split(":")[1]
            : "";
        const size = parseFontSize(value);
        if (size) {
          hasFontSize = true;
        }
      }
      elementStyle = styleArr.join(";");
    }

    const headerMap = {
      H1: "header-one",
      H2: "header-two",
      H3: "header-three",
      H4: "header-four"
    };

    let fontSize = HEADER_FONT_SIZES[headerMap[element.tagName]];
    if (!hasFontSize && fontSize) {
      elementStyle = elementStyle
        ? `${elementStyle};font-size:${fontSize}px;`
        : `font-size:${fontSize}px;`;
    }
    const data = {
      elementStyle
    };

    return {
      data,
      type: headerMap[element.tagName]
    };
  }

  if (element.tagName.startsWith("H")) {
    let elementStyle = element.getAttribute("style");
    elementStyle = processShortHandProperty(elementStyle, element);
    const data = {
      elementStyle
    };
    return {
      data,
      type: "unstyled"
    };
  }

  return null;
};

const HTMLtoDraft = (docHTML, mode = "TEMPLATE") => {
  // Create custom line function dependening omn the mode selected
  // for template mode all ( comment, inserted, deleted and hyperlinks) should be ignored
  // for document only comment should be considered
  // for uploaded document for second version comment, deleted and inserted all should be considered
  const allowedExtraction = DOCUMENT_EXTRACTION[mode];
  let deleted = false;
  let inserted = false;
  let comment = false;
  if (allowedExtraction && Array.isArray(allowedExtraction)) {
    deleted = allowedExtraction.indexOf("deleted") > -1;
    inserted = allowedExtraction.indexOf("inserted") > -1;
    comment = allowedExtraction.indexOf("comments") > -1;
  }

  const customInlineFn = (element, { Style, Entity }) => {
    if (
      deleted &&
      element.tagName === "SPAN" &&
      element.getAttribute("classname") === "deleted"
    ) {
      return Entity(DRAFT_ENTITY.DELETED, { id: element.getAttribute("id") });
    }
    if (
      inserted &&
      element.tagName === "SPAN" &&
      element.getAttribute("classname") === "inserted"
    ) {
      return Entity(DRAFT_ENTITY.INSERTED, { id: element.getAttribute("id") });
    }
    if (
      comment &&
      element.tagName === "SPAN" &&
      element.getAttribute("class") === "comment"
    ) {
      return Entity(DRAFT_ENTITY.COMMENTS, {
        id: element.getAttribute("comment-id")
      });
    }

    const styleMap = {
      B: "BOLD",
      EM: "ITALIC",
      I: "ITALIC",
      STRONG: "BOLD",
      U: "UNDERLINE"
    };

    const styles = [];
    if (element.tagName === "B" && element.style.fontWeight === "normal") {
      // in google docs everything is wrapped in b tag even if its not bold, so we ignore it
      return null;
    } else if (styleMap[element.tagName]) {
      styles.push(styleMap[element.tagName]);
    }

    // 1. Font Color handling
    if (element.style.color) {
      const hexColor = rgb2hex(element.style.color);
      if (hexColor) {
        styles.push(`FONTCOLOR_${hexColor.substring(1, 7)}`);
      }
    }

    // 2. Font Weight handling
    if (element.style.fontWeight) {
      const fontWeight = parseFontWeight(element.style.fontWeight);
      if (fontWeight) {
        styles.push(fontWeight);
      }
    }

    // 3. Font Family handling
    if (element.style.fontFamily) {
      let value = element.style.fontFamily.includes(",")
        ? element.style.fontFamily.split(",")[0]
        : element.style.fontFamily;
      value = value.replace(/"/g, ""); // (ex: ""Courier New"" ==> "Courier New")
      value = value.replace(/'/g, ""); // (ex: "'Courier New'" ==> "Courier New")
      value = value.trim();
      let fontStyle = FONT_FAMILY_OPTIONS_MAP[value];
      if (fontStyle) {
        styles.push(fontStyle);
      }
    }

    // 4. Font Size handling
    if (element.style.fontSize) {
      const size = parseFontSize(element.style.fontSize);
      if (size) {
        styles.push(size);
      }
    }

    // 5. Text Highlight handling
    if (element.style.backgroundColor) {
      const hexColor = rgb2hex(element.style.backgroundColor);
      if (hexColor) {
        styles.push(`FONTTEXTHIGHLIGHT_${hexColor.substring(1, 7)}`);
      }
    }

    // textDecoration underline
    if (
      element.style.textDecoration &&
      element.style.textDecoration === "underline"
    ) {
      styles.push("UNDERLINE");
    }

    if (element.style.fontStyle) {
      if (
        element.style.fontStyle === "oblique" ||
        element.style.fontStyle === "italic"
      ) {
        styles.push("ITALIC");
      }
    }

    if (element.tagName === "A") {
      return Entity(DRAFT_ENTITY.LINK, {
        id: uuid.v4(),
        url: element.getAttribute("href"),
        elementStyle: styles
      });
    }

    if (styles.length) {
      return Style(styles);
    }

    return null;
  };

  const options = {
    customInlineFn,
    customBlockFn,
    keepWhiteSpaceNewLine: true
  };
  //handle special cases like copying from google docs
  let cleanHTML = handleSpecialHTMLCases(docHTML);
  // br is self closing so it doesnt create a block
  cleanHTML = cleanHTML.replace(
    /<p( *\w+=("[^"]*"|'[^']'|[^ >]))*><br\s*\/?><\/p>/gi,
    "<LINE_BREAK> </LINE_BREAK>"
  );
  cleanHTML = cleanHTML.replace(/<br\s*\/?>/gi, "<LINE_BREAK> </LINE_BREAK>");

  //convert html to state
  let contentState = stateFromHTML(cleanHTML, options);
  contentState = convertedElementStylesToInline(contentState);
  return contentState;
};

export default HTMLtoDraft;
